import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import api from "../lib/api";

// Dark split layout matching LoginPage / VerifyEmailPage.
const labelStyle = {
  display: "block",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.75rem",
  fontWeight: 500,
  color: "#8A8A8A",
  letterSpacing: "1px",
  textTransform: "uppercase",
  marginBottom: "0.5rem",
};
const inputStyle = {
  width: "100%",
  padding: "0.85rem 1rem",
  backgroundColor: "#141414",
  border: "1px solid #222",
  borderRadius: "6px",
  color: "#F0EDE8",
  fontFamily: "'Cormorant Garamond', Garamond, serif",
  fontSize: "1rem",
  outline: "none",
  transition: "border-color 0.3s",
};
const errStyle = { marginTop: "0.35rem", fontSize: "0.8rem", color: "#E8593C" };
const focus = (e) => (e.target.style.borderColor = "#C41E1E");
const blur = (e) => (e.target.style.borderColor = "#222");

function Field({ id, label, error, inputProps }) {
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>{label}</label>
      <input id={id} style={inputStyle} onFocus={focus} onBlur={blur} {...inputProps} />
      {error && <p style={errStyle}>{error.message}</p>}
    </div>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [duplicate, setDuplicate] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { name: "", email: "", password: "", confirmPassword: "" },
  });

  const password = watch("password");

  const onSubmit = async (data) => {
    setSubmitting(true);
    setDuplicate(false);
    try {
      await api.post("/client/register", {
        name: data.name,
        email: data.email,
        password: data.password,
      });
      toast.success("Registration successful! Please verify your email.");
      navigate(`/verify?email=${encodeURIComponent(data.email)}`);
    } catch (err) {
      if (err.response?.status === 409) {
        setDuplicate(true);
      } else {
        const msg = err.response?.data?.detail || "Registration failed. Please try again.";
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      {/* Left brand panel */}
      <div
        style={{
          flex: "0 0 55%",
          backgroundColor: "#C41E1E",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "3rem",
          position: "relative",
        }}
        className="hidden md:flex"
      >
        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "3rem", color: "#FFFFFF", letterSpacing: "6px", marginBottom: "1rem" }}>
          ELEMENTAR
        </h1>
        <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(255,255,255,0.4)", marginBottom: "1.5rem" }} />
        <p style={{ fontFamily: "'Cormorant Garamond', Garamond, serif", fontWeight: 400, fontSize: "1.15rem", color: "rgba(255,255,255,0.85)", letterSpacing: "3px", textTransform: "uppercase", textAlign: "center" }}>
          Unified alternative investment intelligence
        </p>
        <p style={{ position: "absolute", bottom: "2rem", fontSize: "0.7rem", letterSpacing: "3px", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>
          From Wooden Owl AG
        </p>
      </div>

      {/* Right form panel */}
      <div style={{ flex: 1, backgroundColor: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
        <div style={{ width: "100%", maxWidth: "380px" }}>
          <div className="md:hidden" style={{ textAlign: "center", marginBottom: "2rem" }}>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "1.8rem", color: "#C41E1E", letterSpacing: "4px" }}>
              ELEMENTAR
            </h2>
          </div>

          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400, fontSize: "1.75rem", color: "#F0EDE8", marginBottom: "0.5rem" }}>
            Create your account
          </h2>
          <p style={{ fontFamily: "'Cormorant Garamond', Garamond, serif", fontSize: "1rem", color: "#8A8A8A", marginBottom: "2rem" }}>
            Create your client account
          </p>

          <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <Field
              id="name"
              label="Name"
              error={errors.name}
              inputProps={{
                type: "text",
                placeholder: "Your full name",
                autoComplete: "name",
                ...register("name", { required: "Name is required" }),
              }}
            />
            <Field
              id="email"
              label="Email"
              error={errors.email}
              inputProps={{
                type: "email",
                placeholder: "you@example.com",
                autoComplete: "email",
                ...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email",
                  },
                }),
              }}
            />
            <Field
              id="password"
              label="Password"
              error={errors.password}
              inputProps={{
                type: "password",
                placeholder: "Minimum 8 characters",
                autoComplete: "new-password",
                ...register("password", {
                  required: "Password is required",
                  minLength: { value: 8, message: "Password must be at least 8 characters" },
                }),
              }}
            />
            <Field
              id="confirmPassword"
              label="Confirm Password"
              error={errors.confirmPassword}
              inputProps={{
                type: "password",
                placeholder: "Re-enter your password",
                autoComplete: "new-password",
                ...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) => value === password || "Passwords do not match",
                }),
              }}
            />

            {duplicate && (
              <p role="alert" style={{ fontSize: "0.85rem", color: "#E8593C" }}>
                An account with this email already exists.{" "}
                <Link to="/login" style={{ color: "#F0EDE8", textDecoration: "underline" }}>
                  Sign in instead
                </Link>
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: "100%",
                padding: "0.85rem",
                backgroundColor: "#C41E1E",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "6px",
                fontSize: "0.8rem",
                fontWeight: 500,
                letterSpacing: "2px",
                textTransform: "uppercase",
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.7 : 1,
                marginTop: "0.5rem",
              }}
            >
              {submitting ? "Creating account..." : "Create Account"}
            </button>
          </form>

          <p style={{ marginTop: "2rem", textAlign: "center", fontSize: "0.85rem", color: "#8A8A8A" }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: "#C41E1E", borderBottom: "1px solid rgba(196,30,30,0.3)", textDecoration: "none" }}>
              Sign in
            </Link>
          </p>

          <div style={{ marginTop: "3rem", textAlign: "center" }}>
            <p style={{ fontSize: "0.7rem", color: "#555", letterSpacing: "1px" }}>🇨🇭 Swiss-domiciled</p>
            <p style={{ fontSize: "0.65rem", color: "#444", marginTop: "0.35rem" }}>© 2026 Elementar</p>
          </div>
        </div>
      </div>
    </div>
  );
}
